<template>
    <page :tabs="tabs" :busy="busy">
        <validation-observer class="d-block p-2" ref="editor_form" v-slot="{ meta }">
            <b-row class="flex-column flex-md-row no-gutters">
                <b-col>
                    <b-alert v-if="!template.is_migrated" show variant="info">
                        It looks like there might be an issue with your HTML content on preview. Simply click the
                        Auto-Fix button below, and we’ll take care of it for you.
                    </b-alert>
                    <b-button @click="resetFontSize" class="mt-1 mb-2" variant="primary"> Auto Fix HTML </b-button>
                </b-col>
            </b-row>
            <validation-provider name="full_html_v2" rules="required" v-slot="{ handleChange }">
                <form-ck-editor-v2 v-model="template.full_html_v2" @ready="insertHtmlComponent" @input="handleChange" />
            </validation-provider>
            <div class="d-flex align-items-center">
                <b-button class="mr-2" :disabled="!meta.valid" variant="primary" @click="confirm">Save</b-button>
                <b-button class="mr-2" :disabled="!meta.valid" variant="default" @click="preview">Preview</b-button>
                <b-button variant="default" @click="goBack">Return</b-button>
            </div>

            <Teleport v-if="htmlMounted" to=".ck-toolbar__items">
                <span
                    role="toolbar"
                    aria-labelledby="cke_430_label"
                    class="ck d-block w-100 ck-reset_all-excluded"
                    id="cke_430"
                >
                    <div class="w-50 search-field" @click="focusInput">
                        <form-search-field
                            class="w-100 mb-0"
                            ref="search_field"
                            :options="htmlTagsOptions"
                            @input="(val) => onSearchTag(val, true)"
                            :busy="loading"
                            :value="tagSearch"
                            @select="selectTag"
                            :rightButton="rightButtonConfig"
                            :rightButton2="rightButtonConfig2"
                            placeholder="Search Tag"
                            @right-button-click="onRightButtonClick"
                            @right-button2-click="insertHtmlTag"
                            :isGrouped="true"
                        />
                    </div>
                </span>
            </Teleport>
            <Teleport v-if="htmlMounted" to=".ck-toolbar__items">
                <span
                    role="toolbar"
                    aria-labelledby="cke_430_label"
                    class="ck d-block w-50 ck-reset_all-excluded"
                    id="cke_430"
                >
                    <template-dropdown @insert-html="insertHtml" :templates="predefinedTemplates" />
                </span>
            </Teleport>
        </validation-observer>
        <modal id="confirm-migration" title="Confirm Migration" @ok="save(true)">
            <p>
                By clicking the "Save" button, you acknowledge that the migrated templates will become the default for
                triggers, emails, and other functionalities. The old templates will still be accessible but will no
                longer be used in active workflows.
            </p>
        </modal>
    </page>
</template>

<script>
import { saveAs } from 'file-saver';

import api from '../../../api';
import { getCookieByName } from '../../../utils/api';
import {
    DOCUMENT_REPORT_TEMPLATE_BY_ID_ENDPOINT,
    DOCUMENT_REPORT_TEMPLATE_TYPE_ENDPOINT,
    DOCUMENT_REPORT_TEMPLATE_VARIABLES_ENDPOINT,
    DOCUMENT_PREDEFINED_TEMPLATES_ENDPOINT,
} from '../../../endpoints';
import { formatResponseError } from '../../../utils/api';
import FormCkEditorV2 from '../../generic/form-ckeditor-v2/form-ckeditor-v2.vue';
import FormInput from '../../generic/form-input/form-input.vue';
import FormSelect from '../../generic/form-select/form-select.vue';
import FormSearchField from '../../generic/form-search-field/form-search-field.vue';
import FormTextarea from '../../generic/form-textarea/form-textarea.vue';
import Grid from '../../generic/grid/grid.vue';
import modal from '../../generic/modal/modal.vue';
import Page from '../../generic/page/page.vue';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
import templateDropdown from '../../generic/template-dropdown/template-dropdown.vue';

export default {
    components: {
        Page,
        Grid,
        ResponsiveButton,
        FormCkEditorV2,
        FormInput,
        FormSelect,
        FormSearchField,
        FormTextarea,
        templateDropdown,
        modal,
    },
    data() {
        return {
            busy: false,
            loading: false,
            html: '',
            tags: [],
            template: {
                report_name: 'New Report Template',
                report_type: '',
                full_html: '',
                full_html_v2: '',
                report_orientation: 165,
                is_default: false,
            },
            tagName: '',
            selectedTag: '',
            htmlMounted: false,
            tagSearch: '',
            htmlTagsOptions: [],
            reportTypeOptions: [],
            editor: null,
            predefinedTemplates: [],
        };
    },
    computed: {
        templateId() {
            return parseInt(this.$route.params.id);
        },
        rightButtonConfig() {
            return {
                icon: ['fas', 'arrow-down'],
                variant: 'default',
            };
        },
        rightButtonConfig2() {
            return {
                variant: 'default',
                text: 'Insert Tag',
            };
        },
        tabs() {
            return [
                {
                    title: 'Edit Report Template',
                    active: false,
                    route: `/app/document/${this.templateId}/edit`,
                },
                {
                    title: `Migrate Template`,
                    active: true,
                },
            ];
        },
    },
    mounted() {
        this.fetchAllData();
    },
    methods: {
        confirm() {
            if (!this.template.is_migrated) {
                this.$bvModal.show('confirm-migration');
            } else {
                this.save(true);
            }
        },
        clearTags() {
            this.selectedTag = '';
            this.tagSearch = '';
        },
        async fetchAllData() {
            await this.fetchReportTemplate();
            await this.fetchReportTypes();
            await this.fetchTags();
            await this.fetchPredefinedTemplates();
            this.$refs.editor_form.setValues({
                report_name: this.template.report_name,
                report_type: this.template.report_type,
                report_orientation: this.template.report_orientation,
                full_html_v2: this.template.full_html_v2,
            });
        },
        resetFontSize() {
            const model = this.editor.model;
            const doc = model.document;

            model.change((writer) => {
                // Select all text
                const range = model.createRange(
                    model.createPositionAt(doc.getRoot(), 0),
                    model.createPositionAt(doc.getRoot(), 'end')
                );
                writer.setSelection(range);

                // Reset font size
                this.editor.execute('fontSize', { value: '14px' }); // Remove font size
                this.editor.execute('fontFamily', { value: `Lato, sans-serif` }); // Remove font family
            });
            this.successToast({ text: 'HTML content has been auto-fixed.' });
        },
        async fetchReportTypes() {
            try {
                const { data } = await api.get(DOCUMENT_REPORT_TEMPLATE_TYPE_ENDPOINT());
                this.reportTypeOptions = data.map((item) => ({
                    value: item.id,
                    text: item.name,
                }));
                if (!this.templateId) {
                    this.template.report_type = this.reportTypeOptions[0].value;
                }
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not fetch report types' });
            } finally {
                this.busy = false;
            }
        },
        onSearchTag(value) {
            if (!value.trim()) {
                this.selectedTag = '';
            }
            const options = this.tags.map((item) => {
                return {
                    text: `${item.tag}: ${item.description}`,
                    value: item.tag,
                    group: item.group.toLowerCase(),
                };
            });
            const filteredOptions = options.filter((item) => item.text.toLowerCase().indexOf(value.toLowerCase()) > 0);
            if (
                options
                    .map((item) => {
                        return {
                            text: `${item.tag}: ${item.description}`,
                            value: item.tag,
                            group: item.group.toLowerCase(),
                        };
                    })
                    .some((item) => item.text.toLowerCase() !== value.toLowerCase())
            ) {
                this.selectedTag = '';
            }
            this.htmlTagsOptions = filteredOptions;
        },
        selectTag(text, value) {
            this.tagSearch = text;
            this.selectedTag = value;
        },
        async fetchReportTemplate() {
            if (this.templateId) {
                try {
                    this.busy = true;
                    const { data } = await api.get(`${DOCUMENT_REPORT_TEMPLATE_BY_ID_ENDPOINT(this.templateId)}`);
                    if (this.templateId) {
                        this.template = data;
                        this.template.full_html_v2 = this.template.full_html_v2 || '';
                        this.$refs.editor_form.setValues({
                            full_html: data.full_html_v2,
                        });
                    }
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not fetch report template' });
                } finally {
                    this.busy = false;
                }
            }
        },
        async fetchTags() {
            try {
                const { data } = await api.get(DOCUMENT_REPORT_TEMPLATE_VARIABLES_ENDPOINT());
                this.htmlTagsOptions = data.map((item) => {
                    return {
                        text: `${item.tag}: ${item.description}`,
                        value: item.tag,
                        group: item.group.toLowerCase(),
                    };
                });
                this.tags = data;
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not fetch tags' });
            } finally {
                this.busy = false;
            }
        },
        async fetchPredefinedTemplates() {
            try {
                const { data } = await api.get(DOCUMENT_PREDEFINED_TEMPLATES_ENDPOINT());
                this.predefinedTemplates = data;
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not fetch predefined templates' });
            } finally {
                this.busy = false;
            }
        },
        focusInput() {
            const search = this.$refs.search_field;
            if (search.$children[0]) {
                if (search.$children[0].$children[0]) {
                    if (search.$children[0].$children[0].$children[0]) {
                        const inputDiv = search.$children[0].$children[0].$children[0].$el;
                        inputDiv.querySelector('input').focus();
                        inputDiv.querySelector('input').select();
                    }
                }
            }
        },
        insertHtmlTag() {
            const editor = this.editor;
            editor.model.change((writer) => {
                const viewFragment = editor.data.processor.toView(this.selectedTag);
                const modelFragment = editor.data.toModel(viewFragment);
                editor.model.insertContent(modelFragment, editor.model.document.selection);
            });
        },
        insertHtml(html) {
            const editor = this.editor;
            editor.model.change((writer) => {
                const viewFragment = editor.data.processor.toView(html);
                const modelFragment = editor.data.toModel(viewFragment);
                editor.model.insertContent(modelFragment, editor.model.document.selection);
            });
        },
        onRightButtonClick() {
            this.tagSearch = '  ';
            if (!this.tagSearch.trim()) {
                this.selectedTag = '';
            }
            const options = this.tags.map((item) => {
                return {
                    text: `${item.tag}: ${item.description}`,
                    value: item.tag,
                    group: item.group.toLowerCase(),
                };
            });
            this.htmlTagsOptions = options;
        },
        insertHtmlComponent(editor) {
            this.htmlMounted = true;
            this.editor = editor;
        },
        async save(isMigrated = false, makePreview = true) {
            try {
                this.busy = true;
                let warning = '';
                if (this.templateId) {
                    const endpoint = `${DOCUMENT_REPORT_TEMPLATE_BY_ID_ENDPOINT(
                        this.templateId
                    )}?make_preview=${makePreview}`;
                    const { data } = await api.patch(endpoint, {
                        ...this.template,
                        is_migrated: isMigrated,
                    });
                    warning = data.warning;
                    this.template = data.data;
                    if (isMigrated) {
                        this.successToast({ text: 'Report template migrated successfully.' });
                        this.$router.push(`/app/document/${this.templateId}/edit/`);
                    }
                }
                if (warning) {
                    this.warningToast({ text: warning });
                }
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not save report template' });
            } finally {
                this.busy = false;
            }
        },
        async preview() {
            try {
                await this.save(false, false);
                this.busy = true;
                const { data } = await api.get(
                    `${DOCUMENT_REPORT_TEMPLATE_BY_ID_ENDPOINT(this.templateId)}preview-v2/`,
                    {
                        responseType: 'blob',
                    }
                );
                saveAs(data, `preview.pdf`);
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({
                    text: generic || 'Could not preview report template. Please double-check your template.',
                });
            } finally {
                this.busy = false;
            }
        },
        goBack() {
            window.location.href = `/app/document/templates/`;
        },
    },
};
</script>

<style></style>

<style lang="scss" scoped>
.template-icon {
    width: 20px;
    height: 20px;
}
:deep(.cke_toolbar fieldset) {
    border: none !important;
    padding: 0;
}

:deep(.cke_reset_all .input-group-append svg) {
    width: 10px;
}

:deep(.search-field) {
    .subscribers-list-wrapper {
        top: auto;
    }

    input {
        padding: 2px 5px !important;
        font-size: 12px !important;
    }

    .merge-guests-wrapper {
        width: auto !important;

        @media (max-width: 600px) {
            max-width: 80% !important;
        }
    }
}
</style>
